/** SPEC §16: audit-log (omschrijving, verschil) en bewaartermijnen. */
import { addDagen } from './datum';
import { RESPIJT_DAGEN, type Abonnement } from './licentie';
import type { ISODate } from './types';

export const AUDIT_BEWAAR_MAANDEN = 12;
export const OPSCHONEN_NA_MAANDEN = 3;

/** Datum + n maanden (laatste dag van de maand als de dag niet bestaat). */
export function addMaanden(d: ISODate, n: number): ISODate {
  const [j, m, dag] = d.split('-').map(Number);
  const totaal = j * 12 + (m - 1) + n;
  const nj = Math.floor(totaal / 12);
  const nm = totaal % 12;
  const laatste = new Date(Date.UTC(nj, nm + 1, 0)).getUTCDate();
  return `${nj}-${String(nm + 1).padStart(2, '0')}-${String(Math.min(dag, laatste)).padStart(2, '0')}` as ISODate;
}

export interface BewaarBron {
  abonnement: Abonnement | string;
  trialTot: ISODate | null;
  abonnementStatus: string | null;
  betaaldTot: ISODate | null;
  bewaarTot?: ISODate | null;
}

/** SPEC §16.3: einde van de omgeving (null = geen automatisch einde). */
export function eindeOmgeving(t: BewaarBron): ISODate | null {
  if (t.abonnement === 'TRIAL') return t.trialTot;
  if (t.abonnement === 'STRIPE') {
    const st = t.abonnementStatus ?? '';
    if (st === 'canceled') return t.betaaldTot;
    if (st === 'past_due' || st === 'unpaid') return t.betaaldTot ? addDagen(t.betaaldTot, RESPIJT_DAGEN) : null;
  }
  return null;
}

/** SPEC §16.3: datum waarop de organisatie met al haar gegevens wordt verwijderd (null = niet automatisch). */
export function verwijderDatum(t: BewaarBron, maanden = OPSCHONEN_NA_MAANDEN): ISODate | null {
  const einde = eindeOmgeving(t);
  if (!einde) return null;
  const d = addMaanden(einde, maanden);
  return t.bewaarTot && t.bewaarTot > d ? t.bewaarTot : d;
}

// ---------------- verschil oud → nieuw ----------------

const TECHNISCH = /^(id|tenantId|aangemaakt|gewijzigd|bijgewerkt|sync|outlookEventId|outlookEvents|agendaId|lat|lon|geoAdres|geoNaam|hash|laatstGezien|laatstGebruikt|volgorde)$/;
export const GEHEIM = /wachtwoord|password|token|secret|geheim|sleutel|hash|verifier/i;
const MAX = 200;

export type VeldWijziging = { van: unknown; naar: unknown };

const kort = (v: unknown): unknown => {
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'string' && v.length > MAX) return v.startsWith('data:') ? '(bestand)' : v.slice(0, MAX) + '…';
  if (v && typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > MAX ? s.slice(0, MAX) + '…' : v;
  }
  return v ?? null;
};
const gelijk = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);

/** Gewijzigde velden tussen twee versies van een record; objecten (jsonb) één niveau diep als `veld.sub`. */
export function verschil(oud: Record<string, unknown> | null | undefined, nieuw: Record<string, unknown> | null | undefined): Record<string, VeldWijziging> {
  const uit: Record<string, VeldWijziging> = {};
  if (!oud || !nieuw) return uit;
  for (const k of new Set([...Object.keys(oud), ...Object.keys(nieuw)])) {
    if (TECHNISCH.test(k) || GEHEIM.test(k)) continue;
    const a = oud[k];
    const b = nieuw[k];
    if (gelijk(a, b)) continue;
    if (isObject(a) || isObject(b)) {
      const ao = isObject(a) ? a : {};
      const bo = isObject(b) ? b : {};
      for (const s of new Set([...Object.keys(ao), ...Object.keys(bo)])) {
        if (GEHEIM.test(s) || gelijk(ao[s], bo[s])) continue;
        uit[`${k}.${s}`] = { van: kort(ao[s]), naar: kort(bo[s]) };
      }
      continue;
    }
    uit[k] = { van: kort(a), naar: kort(b) };
  }
  return uit;
}

/** Invoer (body) zonder geheimen en lange waarden, voor in het log. */
export function veiligeInvoer(v: unknown, diepte = 0): unknown {
  if (Array.isArray(v)) return v.length > 20 ? `(${v.length} items)` : v.map((x) => veiligeInvoer(x, diepte + 1));
  if (isObject(v)) {
    if (diepte > 2) return '(…)';
    const uit: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) if (!GEHEIM.test(k)) uit[k] = veiligeInvoer(x, diepte + 1);
    return uit;
  }
  return kort(v);
}

// ---------------- acties en omschrijving ----------------

/** Meervoud in de route → enkelvoud in de actie. */
const ENTITEIT: Record<string, string> = {
  projecten: 'project', taken: 'taak', engineers: 'engineer', gebruikers: 'gebruiker', werkpakketten: 'werkpakket',
  takenpakketten: 'takenpakket', sprints: 'sprint', items: 'item', afwezigheden: 'afwezigheid', feestdagen: 'feestdag',
  expertises: 'expertise', templates: 'template', 'tijd.regels': 'uren', 'tijd.sleutels': 'sleutel', 'tijd.cel': 'uren.cel',
  'tijd.rest': 'uren.rest', 'tijd.agenda': 'uren.agenda', 'tijd.start': 'uren.timer.start', 'tijd.stop': 'uren.timer.stop', 'tijd.goedkeuren': 'uren.goedkeuren',
  'organisatie.huisstijl': 'huisstijl', opmerkingen: 'opmerking', bijlagen: 'bijlage', 'eigen-velden': 'eigen-veld', 'projecten.baselines': 'baseline',
  baselines: 'baseline', 'api-sleutels': 'api-sleutel', webhooks: 'webhook', 'v1.webhooks': 'webhook', 'agenda.mijn.ontkoppelen': 'agenda.ontkoppeld', 'beheer.processen': 'proces.gestart', 'beheer.testmail': 'testmail',
};
const WERKWOORDEN = new Set(['test', 'start', 'stop', 'afsluiten', 'verplaats', 'vervolg', 'opnieuw', 'verleng', 'volgorde', 'template', 'koppellink', 'herstel', 'ontkoppelen', 'outlook-ontkoppelen', 'afrekenen', 'wijzigen', 'portaal', 'controleer', 'prognose', 'goedkeuren', 'testmail', 'processen', 'agenda', 'cel', 'rest']);

/** Actie uit methode en route-patroon, bijv. DELETE /api/werkpakketten/:id → werkpakket.verwijder. */
export function actieVanRoute(methode: string, route: string): string {
  const delen = route.replace(/^\/api\//, '').split('/').filter(Boolean);
  const vast = delen.filter((d) => !d.startsWith(':'));
  const laatsteIsVast = delen.length > 0 && !delen[delen.length - 1].startsWith(':');
  const sleutel = vast.join('.');
  if (ENTITEIT[sleutel]) {
    const e = ENTITEIT[sleutel];
    if (methode === 'DELETE') return `${e}.verwijder`;
    if (methode === 'PUT' || methode === 'PATCH') return e.includes('.') ? e : `${e}.wijzig`;
    return e.includes('.') || WERKWOORDEN.has(vast[vast.length - 1]) ? e : `${e}.nieuw`;
  }
  // entiteit + werkwoord, bijv. taken/:id/verplaats of sprints/:id/start
  const basis = vast.slice(0, -1).join('.');
  const ww = vast[vast.length - 1];
  if (laatsteIsVast && vast.length > 1 && (ENTITEIT[basis] || WERKWOORDEN.has(ww))) return `${ENTITEIT[basis] ?? basis}.${ww}`;
  const e = ENTITEIT[sleutel] ?? sleutel;
  if (methode === 'DELETE') return `${e}.verwijder`;
  if (methode === 'PUT' || methode === 'PATCH') return `${e}.wijzig`;
  return laatsteIsVast && WERKWOORDEN.has(ww) ? e : `${e}.nieuw`;
}

const ONDERWERP: Record<string, string> = {
  project: 'Project', taak: 'Taak', engineer: 'Engineer', gebruiker: 'Gebruiker', werkpakket: 'Werkpakket', takenpakket: 'Takenpakket',
  sprint: 'Sprint', item: 'Backlog-item', afwezigheid: 'Afwezigheid', feestdag: 'Feestdag', expertise: 'Expertise', template: 'Template',
  uren: 'Uren', 'uren.cel': 'Uren (weekstaat)', 'uren.timer': 'Timer', sleutel: 'Persoonlijke sleutel', instellingen: 'Instellingen',
  organisatie: 'Organisatie', huisstijl: 'Huisstijl', abonnement: 'Abonnement', agenda: 'Agenda', 'agile': 'Agile', proces: 'Proces',
  opmerking: 'Opmerking', bijlage: 'Bijlage', 'eigen-veld': 'Eigen veld', baseline: 'Baseline', 'api-sleutel': 'API-sleutel', webhook: 'Webhook',
  platform: 'Platformbeheer', 'platform.organisatie': 'Organisatie (platform)', 'platform.gebruiker': 'Gebruiker (platform)', 'platform.demo': 'Demo (platform)',
};
const WERKWOORD: Record<string, string> = {
  nieuw: 'aangemaakt', wijzig: 'gewijzigd', verwijder: 'verwijderd', start: 'gestart', stop: 'gestopt', afgesloten: 'afgesloten', afsluiten: 'afgesloten',
  verplaats: 'verplaatst', vervolg: 'vervolgtaak gemaakt', opnieuw: 'opnieuw ingepland', verleng: 'verlengd', volgorde: 'volgorde gewijzigd',
  template: 'template toegepast', koppellink: 'koppellink gestuurd', gekoppeld: 'gekoppeld', ontkoppeld: 'ontkoppeld', 'outlook-ontkoppelen': 'Outlook ontkoppeld',
  herstel: 'standaard hersteld', goedgekeurd: 'goedgekeurd', heropend: 'heropend', goedkeuren: 'goedkeuren', checkout: 'afrekenen gestart',
  factuur: 'op factuur afgesloten', afrekenen: 'afrekenen gestart', wijzigen: 'gewijzigd', portaal: 'klantportaal geopend', prognose: 'prognose berekend',
  gestart: 'handmatig gestart', meekijken: 'meekijken gestart', opnieuw_demo: 'opnieuw opgebouwd', rest: 'resterende uren bijgewerkt', cel: 'bijgewerkt',
  agenda: 'uit agenda overgenomen', test: 'test verstuurd',
};

/** Leesbare omschrijving van een actie, bijv. `taak.wijzig` → "Taak gewijzigd". */
export function auditOmschrijving(actie: string): string {
  if (actie === 'inlog') return 'Ingelogd';
  if (actie === 'uitlog') return 'Uitgelogd';
  if (actie === 'testmail') return 'Testmail verstuurd';
  if (actie === 'platform.meekijken') return 'Platformbeheer kijkt mee';
  if (actie === 'organisatie.gewisseld') return 'Gewisseld naar andere organisatie';
  const delen = actie.split('.');
  for (let i = delen.length - 1; i > 0; i--) {
    const ond = delen.slice(0, i).join('.');
    if (ONDERWERP[ond]) {
      const rest = delen.slice(i).join(' ');
      const ww = WERKWOORD[rest] ?? rest.replace(/-/g, ' ');
      return `${ONDERWERP[ond]} ${ww}`;
    }
  }
  return actie;
}
